import MonitorIcon from '@mui/icons-material/Monitor';
import CrossBrowserIcon from '@mui/icons-material/Devices';
import MobileIcon from '@mui/icons-material/PhoneIphone';
import LiveTestingIcon from '@mui/icons-material/AdsClick';
import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import makeStyles from '@mui/styles/makeStyles';
import Typography from '@mui/material/Typography';
import { FormattedMessage } from 'react-intl';
import { Divider } from '@katalon-studio/katalon-ui/v2';
import { useLaunchDarkly } from '../../../launchdarkly';
import { fromOrder, fromSubscription } from '../../../store';
import { useQuery } from '../../../routes';
import { Feature, getGroupDisplayColor, ProductGroup as Group, SubscriptionSource } from '../../../models';
import ProductItem, { ProductItemProps } from './ProductItem';
import { ProductGroup } from '..';
import directSaleFeature = Feature.directSaleFeature;
import { ContactSaleBtn } from '../../../components';

const useStyle = makeStyles(theme => ({
  contactSaleBox: {
    margin: theme.spacing(2),
    [theme.breakpoints.down('mobile')]: {
      width: '100%',
    },
    [theme.breakpoints.up('mobile')]: {
      width: '50%',
    },
  },
  contactSaleBoxContent: {
    height: '80%',
  },
}));
interface ProductGroupItem {
  groupId: string,
  color: string,
  products: Array<ProductItemProps>,
}

export const ProductSelection = () => {
  const { flags } = useLaunchDarkly();
  const { get } = useQuery();
  const accountId = Number(get('accountId'));
  const classes = useStyle();
  const subscriptions = useSelector((fromSubscription.selectByAccountId(accountId)));
  const orderPreview = useSelector(fromOrder.selectOrderPreviewResponse);
  const hasKSESubscription = subscriptions.some(
    subscription => subscription.feature === Feature.PER_USER_KSE
      && subscription.source === SubscriptionSource.RECURLY,
  );
  const hasPreviewKSE = orderPreview?.productsPreview.some(
    productPreview => productPreview.feature === Feature.PER_USER_KSE,
  ) ?? false;

  const disabledOtherProduct = !hasKSESubscription && !hasPreviewKSE;
  const testCloudSubProducts = [
    {
      subProductIcon: <MonitorIcon />,
      subProductId: 'testcloud_session_webdesktop',
      unitPrice: {
        MONTH: flags?.subscriptionPlanPrice?.testCloudDesktopMonthly,
        YEAR: flags?.subscriptionPlanPrice?.testCloudDesktopAnnually,
      },
      disabled: disabledOtherProduct,
    },
    {
      subProductIcon: <CrossBrowserIcon />,
      subProductId: 'testcloud_session_crossbrowser',
      unitPrice: {
        MONTH: flags?.subscriptionPlanPrice?.testCloudCrossBrowserMonthly,
        YEAR: flags?.subscriptionPlanPrice?.testCloudCrossBrowserAnnually,
      },
      disabled: disabledOtherProduct,
    },
    {
      subProductIcon: <MobileIcon />,
      subProductId: 'testcloud_session_mobileapp',
      unitPrice: {
        MONTH: flags?.subscriptionPlanPrice?.testCloudMobileMonthly,
        YEAR: flags?.subscriptionPlanPrice?.testCloudMobileAnnually,
      },
      disabled: disabledOtherProduct,
    },
    {
      subProductIcon: <LiveTestingIcon />,
      subProductId: 'testcloud_session_livetesting',
      unitPrice: {
        MONTH: flags?.subscriptionPlanPrice?.testCloudLiveTestingMonthly,
        YEAR: flags?.subscriptionPlanPrice?.testCloudLiveTestingAnnually,
      },
      disabled: disabledOtherProduct,
    },
  ];

  const productGroups: Array<ProductGroupItem> = [
    {
      groupId: Group.TEST_CREATION,
      color: getGroupDisplayColor(Group.TEST_CREATION),
      products: [
        {
          productId: 'kse_per-user',
          unitPrice: {
            MONTH: flags?.subscriptionPlanPrice?.kseMonthly,
            YEAR: flags?.subscriptionPlanPrice?.kseAnnually,
          },
          initQuota: !hasKSESubscription ? 1 : undefined,
          tooltipText: 'page.expand.plan.product.quota.decrease.disabled.tooltip',
          disabled: false,
        },
      ],
    },
    {
      groupId: Group.TEST_EXECUTION,
      color: getGroupDisplayColor(Group.TEST_EXECUTION),
      products: [
        {
          productId: 'kre_floating',
          unitPrice: {
            MONTH: flags?.subscriptionPlanPrice?.kreMonthly,
            YEAR: flags?.subscriptionPlanPrice?.kreAnnually,
          },
          disabled: disabledOtherProduct,
        },
        {
          productId: 'testcloud',
          subProducts: testCloudSubProducts,
          disabled: disabledOtherProduct,
        },
      ],
    },
  ];

  return (
    <Box
      id="product-selection-box"
      height="100%"
      display="flex"
      flexDirection="column"
      overflow="scroll"
      sx={theme => ({
        [theme.breakpoints.up('tablet')]: {
          paddingRight: theme.spacing(3),
          marginRight: theme.spacing(1),
        },
      })}
    >
      <Box
        id="self-serve-product-selection-box"
        display="flex"
        flexDirection="column"
      >
        {productGroups.map(group => (
          <ProductGroup
            key={group.groupId}
            groupId={group.groupId}
            color={group.color}
          >
            {group.products.map(product => (
              <Box key={product.productId}>
                <ProductItem
                  key={product.productId}
                  productId={product.productId}
                  unitPrice={product.unitPrice}
                  subProducts={product.subProducts}
                  initQuota={product.initQuota}
                  tooltipText={product.tooltipText}
                  disabled={product.disabled}
                />
              </Box>
            ))}
          </ProductGroup>
        ))}
      </Box>
      <Divider />
      <Box
        id="contact-sale-product-selection-box"
        display="flex"
        flexDirection="row"
      >
        {directSaleFeature.map(feature => (
          <Box
            key={`contact-sale-product-${feature}`}
            display="flex"
            flexDirection="column"
            className={classes.contactSaleBox}
          >
            <Box
              id={`product-${feature}-header`}
            >
              <Typography
                variant="subtitle1"
                fontWeight={700}
                fontSize={14}
                textTransform="uppercase"
                color={
                  getGroupDisplayColor(Feature.getFeatureGroup(feature)) ?? '#727993'
}
              >
                <FormattedMessage id={`product.group.${Feature.getFeatureGroup(feature)}.title`} />
              </Typography>
            </Box>
            <Box
              id={`product-${feature.toLowerCase()}`}
              key={`producte-${feature.toLowerCase()}`}
              display="flex"
              flexDirection="column"
              width="100%"
              className={classes.contactSaleBoxContent}
            >
              <Box
                display="flex"
                flexDirection="column"
                paddingBottom={1}
              >
                <Typography
                  variant="body1"
                  fontWeight={600}
                  fontSize={18}
                  sx={theme => ({
                    paddingBottom: theme.spacing(1),
                  })}
                >
                  <FormattedMessage
                    id={`feature.${feature.toLowerCase()}.product.name`}
                  />
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={400}
                  fontSize={16}
                >
                  <FormattedMessage
                    id={`feature.${feature.toLowerCase()}.product.desc`}
                  />
                </Typography>
              </Box>
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="flex-start"
              justifySelf="self-end"
              paddingTop={2}
              paddingBottom={2}
            >
              <ContactSaleBtn />
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
